import { default as React } from 'react';
import { Grid, Chip } from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityLayout,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  EntityRelationWarning,
  EntitySwitch,
  hasCatalogProcessingErrors,
  hasRelationWarnings,
  isOrphan,
} from '@backstage/plugin-catalog';
import { useEntity, catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  useApi,
  configApiRef,
  alertApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import {
  EntityArgoCDOverviewCard,
  isArgocdAvailable,
} from '@roadiehq/backstage-plugin-argo-cd';
import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';
import {
  ManageOwnersButton,
  MicroappAvailabilityCard,
  PodLogsModal,
  SecretsTab,
} from '../catalog/microapps';
import {
  useCurrentUserEmail,
  isUserAuthorizedForEntity,
} from '../catalog/microapps/hooks';
import { useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import BuildIcon from '@mui/icons-material/Build';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import PortIcon from '@mui/icons-material/SettingsEthernet';
import { Entity } from '@backstage/catalog-model';

const MCP_ENDPOINT_ANNOTATION = 'microapp/endpoint';
const MCP_TRANSPORT_ANNOTATION = 'microapp/transport';
const MCP_CREATED_BY_ANNOTATION = 'microapp/createdBy';
const MCP_TOOLS_ANNOTATION = 'microapp/tools';

function getMcpEndpoint(entity: Entity): string {
  return entity.metadata.annotations?.[MCP_ENDPOINT_ANNOTATION] ?? '';
}

function getMcpTransport(entity: Entity): string {
  return (
    entity.metadata.annotations?.[MCP_TRANSPORT_ANNOTATION] ?? 'streamable-http'
  );
}

function getMcpCreatedBy(entity: Entity): string {
  return entity.metadata.annotations?.[MCP_CREATED_BY_ANNOTATION] ?? '';
}

function getMcpTools(
  entity: Entity,
): Array<{ name: string; description: string }> {
  const raw = entity.metadata.annotations?.[MCP_TOOLS_ANNOTATION];
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t): t is { name: string; description: string } =>
        typeof t?.name === 'string' && typeof t?.description === 'string',
    );
  } catch {
    return [];
  }
}

function getTeamName(entity: Entity): string {
  const owner = entity.spec?.owner as string | undefined;
  if (!owner) return '';
  return owner.split('/').pop()?.split(':').pop() || owner;
}

function buildAddCommand(entity: Entity): string {
  const endpoint = getMcpEndpoint(entity);
  const transport = getMcpTransport(entity);
  const transportFlag = transport === 'streamable-http' ? 'http' : transport;
  return `claude mcp add --transport ${transportFlag} --callback-port 9876 ${entity.metadata.name} ${endpoint}`;
}

// MicroappConfigCard-matching row styles
const iconStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  verticalAlign: 'middle',
  marginRight: 4,
};
const rowSx = { display: 'flex', mb: 1.5, alignItems: 'flex-start' } as const;
const labelSx = {
  color: 'rgba(0,0,0,0.54)',
  fontWeight: 'bold',
  fontSize: '10px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  width: 180,
  flexShrink: 0,
  paddingTop: '2px',
  overflow: 'hidden',
  whiteSpace: 'nowrap' as const,
};
const valueSx = {
  fontSize: '0.875rem',
  flex: 1,
  wordBreak: 'break-word' as const,
};

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const McpServerOverview = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const configApi = useApi(configApiRef);
  const alertApi = useApi(alertApiRef);
  const identityApi = useApi(identityApiRef);
  const navigate = useNavigate();

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [logsOpen, setLogsOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isRedeploying, setIsRedeploying] = React.useState(false);

  const currentUserEmail = useCurrentUserEmail();
  const isAuthorized = isUserAuthorizedForEntity(entity, currentUserEmail);
  const teamName = getTeamName(entity);
  const createdBy = getMcpCreatedBy(entity);
  const isActive = entity.spec?.lifecycle === 'production';
  const addCommand = buildAddCommand(entity);
  const tools = getMcpTools(entity);

  const handleCopyCommand = () => {
    navigator.clipboard
      .writeText(addCommand)
      .then(() => {
        setSnackbarOpen(true);
      })
      .catch(() => {
        // clipboard access denied
      });
  };

  const handleRedeploy = async () => {
    setIsRedeploying(true);
    try {
      const sourceLocation =
        entity.metadata.annotations?.['backstage.io/source-location'] ?? '';
      const githubRepoUrl = sourceLocation.replace(/^url:/, '');

      // Refresh the catalog entity to get the latest annotations
      // (handles the case where another owner changed secrets after this page loaded)
      await catalogApi.refreshEntity(
        `component:default/${entity.metadata.name}`,
      );
      const freshEntity = await catalogApi.getEntityByRef(
        `component:default/${entity.metadata.name}`,
      );
      const ann =
        freshEntity?.metadata?.annotations ?? entity.metadata.annotations ?? {};

      const enableSecrets = ann['microapp/hasGsmSecrets'] === 'true';
      const secretKeys = (ann['microapp/gsmSecretKeys'] ?? '')
        .split(',')
        .map((k: string) => k.trim())
        .filter(Boolean);
      const secrets =
        enableSecrets && secretKeys.length > 0
          ? secretKeys.map((k: string) => ({ key: k, value: '' }))
          : undefined;

      const containerPort =
        Number(entity.metadata.annotations?.['microapp/containerPort']) || 3001;
      const downstreamOAuth =
        entity.metadata.annotations?.['microapp/downstreamOAuth'] === 'true';

      const formData = {
        actionType: 'redeploy',
        githubRepoUrl: { value: githubRepoUrl },
        containerPort,
        downstreamOAuth,
        enableSecrets,
        ...(enableSecrets && secrets && { secrets }),
      };
      navigate(
        `/create/templates/default/mcp-server-hosting?formData=${encodeURIComponent(
          JSON.stringify(formData),
        )}`,
      );
    } finally {
      setIsRedeploying(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const backendUrl = configApi.getString('backend.baseUrl');
      const { token } = await identityApi.getCredentials();
      const response = await fetch(
        `${backendUrl}/api/microapps-api/unregister`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            appName: entity.metadata.name,
            entityName: entity.metadata.name,
            entityKind: entity.kind,
            entityNamespace: entity.metadata.namespace || 'default',
            appType: 'mcp-server',
          }),
        },
      );

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete MCP server');
      }

      alertApi.post({
        message: `Successfully deleted ${
          entity.metadata.title || entity.metadata.name
        }. Redirecting...`,
        severity: 'success',
        display: 'transient',
      });
      setTimeout(() => {
        window.location.href = '/mcp-servers';
      }, 1500);
    } catch (err) {
      alertApi.post({
        message: `Failed to delete: ${(err as Error).message}`,
        severity: 'error',
      });
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="stretch">
      {entityWarningContent}

      {/* Left column: Getting Started + About */}
      <Grid item md={7} xs={12}>
        {/* Getting Started */}
        <Card style={{ marginBottom: 16 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Getting Started
            </Typography>
            <Typography
              variant="body2"
              gutterBottom
              sx={{
                fontFamily: 'monospace',
                color: '#228189',
                fontWeight: 600,
              }}
            >
              Add this MCP server to Claude Code with one command:
            </Typography>
            {/* Dark terminal-style code block */}
            <Box
              sx={{
                bgcolor: '#1e1e2e',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {/* Code block header bar */}
              <Box
                sx={{
                  bgcolor: '#2a2a3e',
                  px: 2,
                  py: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: '#888', fontFamily: 'monospace' }}
                >
                  Terminal
                </Typography>
                <Tooltip
                  title={snackbarOpen ? 'Copied!' : 'Copy command'}
                  placement="left"
                >
                  <Button
                    size="small"
                    onClick={handleCopyCommand}
                    startIcon={
                      <ContentCopyIcon sx={{ fontSize: '14px !important' }} />
                    }
                    sx={{
                      color: '#98c379',
                      fontSize: 11,
                      textTransform: 'none',
                      minWidth: 0,
                      px: 1,
                      py: 0.25,
                      border: '1px solid rgba(152,195,121,0.4)',
                      borderRadius: 0.5,
                      '&:hover': {
                        bgcolor: 'rgba(152,195,121,0.1)',
                        border: '1px solid rgba(152,195,121,0.8)',
                      },
                    }}
                    aria-label="Copy add command"
                  >
                    Copy
                  </Button>
                </Tooltip>
              </Box>
              {/* Command text */}
              <Box
                sx={{
                  p: 2,
                  fontFamily: 'monospace',
                  fontSize: 13,
                  color: '#98c379',
                  wordBreak: 'break-all',
                  lineHeight: 1.6,
                }}
              >
                {addCommand}
              </Box>
            </Box>
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              mt={1.5}
            >
              On the first run this opens a browser window for WellSky SSO
              authentication. After that, it's automatic.
            </Typography>
          </CardContent>
        </Card>

        {/* Available Tools */}
        {tools.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <CardContent>
              {/* Heading with wrench icon */}
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <BuildIcon sx={{ fontSize: 20, color: '#228189' }} />
                <Typography variant="h6" fontWeight={600}>
                  Available Tools
                </Typography>
              </Box>
              <Divider style={{ marginBottom: 4 }} />
              {tools.map((tool, idx) => (
                <Box
                  key={tool.name}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 3,
                    py: 1.5,
                    borderBottom: idx < tools.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  {/* Tool name — plain teal monospace text, no box */}
                  <Typography
                    component="code"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      fontSize: 13,
                      color: '#228189',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      minWidth: 160,
                    }}
                  >
                    {tool.name}
                  </Typography>
                  {/* Description */}
                  <Typography variant="body2" color="textSecondary">
                    {tool.description}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Availability card — wide, so placed in left column */}
        <Box mt={2}>
          <MicroappAvailabilityCard />
        </Box>
      </Grid>

      {/* Right column: Application Configuration + ArgoCD */}
      <Grid item md={5} xs={12}>
        <InfoCard title="Application Configuration" noPadding={false}>
          {/* App Type */}
          <Box sx={rowSx}>
            <Typography sx={labelSx}>App Type</Typography>
            <Box sx={{ flex: 1 }}>
              <Chip label="MCP Server" size="small" color="primary" />
            </Box>
          </Box>

          {/* Owner */}
          {teamName && (
            <Box sx={rowSx}>
              <Typography sx={labelSx}>
                <PersonIcon style={iconStyle} />
                Owner
              </Typography>
              <Typography sx={valueSx}>{teamName}</Typography>
            </Box>
          )}

          {/* Status */}
          <Box sx={rowSx}>
            <Typography sx={labelSx}>Status</Typography>
            <Box
              sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: isActive ? 'success.main' : 'text.disabled',
                }}
              />
              <Typography sx={valueSx}>
                {isActive ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
          </Box>

          {/* Requires VPN — MCP servers always require WellSky SSO/VPN */}
          <Box sx={rowSx}>
            <Typography sx={labelSx}>Requires VPN</Typography>
            <Typography sx={valueSx}>Yes</Typography>
          </Box>

          {/* Container Port */}
          {entity.metadata.annotations?.['microapp/containerPort'] && (
            <Box sx={rowSx}>
              <Typography sx={labelSx}>
                <PortIcon style={iconStyle} />
                Container Port
              </Typography>
              <Typography sx={valueSx}>
                {entity.metadata.annotations['microapp/containerPort']}
              </Typography>
            </Box>
          )}

          {/* Workload Identity */}
          {entity.metadata.annotations?.['microapp/serviceAccount'] &&
            entity.metadata.annotations?.['kubernetes.io/namespace'] && (
              <Box sx={rowSx}>
                <Typography sx={labelSx}>
                  <AccountBoxIcon style={iconStyle} />
                  Workload Identity
                </Typography>
                <Typography
                  sx={{
                    ...valueSx,
                    fontFamily: 'monospace',
                    fontSize: '0.72rem',
                    wordBreak: 'break-all',
                  }}
                >
                  {`principal://iam.googleapis.com/projects/791409896686/locations/global/workloadIdentityPools/plateng-prod-app-wsky.svc.id.goog/subject/ns/${entity.metadata.annotations['kubernetes.io/namespace']}/sa/${entity.metadata.annotations['microapp/serviceAccount']}`}
                </Typography>
              </Box>
            )}

          {/* Created by */}
          {createdBy && (
            <Box sx={rowSx}>
              <Typography sx={labelSx}>
                <PersonIcon style={iconStyle} />
                Created by
              </Typography>
              <Typography sx={valueSx}>{createdBy}</Typography>
            </Box>
          )}

          {/* Created at */}
          {entity.metadata.annotations?.['microapp/createdAt'] && (
            <Box sx={rowSx}>
              <Typography sx={labelSx}>
                <CalendarTodayIcon style={iconStyle} />
                Created at
              </Typography>
              <Typography sx={valueSx}>
                {new Date(
                  entity.metadata.annotations['microapp/createdAt'],
                ).toLocaleString()}
              </Typography>
            </Box>
          )}
          {/* Managed Secrets */}
          {entity.metadata.annotations?.['microapp/hasGsmSecrets'] && (
            <>
              <Box
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  mt: 1.5,
                  pt: 1.5,
                }}
              >
                <Typography
                  sx={{
                    color: 'rgba(0,0,0,0.54)',
                    fontWeight: 'bold',
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 1,
                  }}
                >
                  SkyStage Managed Secrets
                </Typography>
                {entity.metadata.annotations['microapp/hasGsmSecrets'] ===
                'true' ? (
                  entity.metadata.annotations['microapp/gsmSecretKeys']
                    ?.split(',')
                    .map(k => k.trim())
                    .filter(Boolean)
                    .map(key => (
                      <Box key={key} sx={{ ...rowSx, mb: 0.75 }}>
                        <Typography sx={labelSx}>{key}</Typography>
                        <Chip label="Managed" size="small" color="primary" />
                      </Box>
                    )) ?? (
                    <Typography sx={valueSx}>No keys configured</Typography>
                  )
                ) : (
                  <Typography
                    sx={valueSx}
                    style={{ color: 'rgba(0,0,0,0.54)' }}
                  >
                    No secrets
                  </Typography>
                )}
              </Box>
            </>
          )}
        </InfoCard>

        {/* Action buttons: Redeploy | View Pod Logs | Delete — equal-width row */}
        <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={
              isRedeploying ? <CircularProgress size={16} /> : <ReplayIcon />
            }
            onClick={handleRedeploy}
            disabled={isRedeploying}
            sx={{ textTransform: 'none', flex: 1 }}
          >
            {isRedeploying ? 'Loading...' : 'Redeploy'}
          </Button>
          {entity.metadata.annotations?.['argocd/app-name'] &&
            entity.metadata.annotations?.['kubernetes.io/namespace'] && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<DescriptionIcon />}
                onClick={() => setLogsOpen(true)}
                sx={{ textTransform: 'none', flex: 1 }}
              >
                View Pod Logs
              </Button>
            )}
          <Button
            variant="contained"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            sx={{
              textTransform: 'none',
              flex: 1,
              bgcolor: 'error.main',
              '&:hover': { bgcolor: 'error.dark' },
            }}
          >
            Delete Server
          </Button>
        </Box>

        {/* Manage Owners — same as microapps, visible to owners only */}
        {isAuthorized && (
          <Box mt={1}>
            <ManageOwnersButton
              entity={entity}
              currentUserEmail={currentUserEmail}
              isAuthorized={isAuthorized}
            />
          </Box>
        )}

        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete{' '}
              <strong>{entity.metadata.title || entity.metadata.name}</strong>?
              <br />
              <br />
              This will:
              <ul>
                <li>Remove Kubernetes manifests from the argo repository</li>
                <li>Remove the ArgoCD application</li>
                <li>Remove the server from the catalog</li>
              </ul>
              <strong>This action cannot be undone.</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={isDeleting}
              startIcon={
                isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />
              }
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Pod logs modal */}
        {entity.metadata.annotations?.['argocd/app-name'] &&
          entity.metadata.annotations?.['kubernetes.io/namespace'] && (
            <PodLogsModal
              open={logsOpen}
              onClose={() => setLogsOpen(false)}
              appName={entity.metadata.name}
              argoCdAppName={entity.metadata.annotations['argocd/app-name']}
              namespace={entity.metadata.annotations['kubernetes.io/namespace']}
            />
          )}

        {/* ArgoCD overview */}
        <EntitySwitch>
          <EntitySwitch.Case if={isArgocdAvailable}>
            <EntityArgoCDOverviewCard />
          </EntitySwitch.Case>
        </EntitySwitch>

        {/* About card — in right column */}
        <Box mt={2}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityAboutCard variant="gridItem" />
        </Box>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Add command copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Grid>
  );
};

export const McpServerEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <McpServerOverview />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/secrets"
      title="Secrets"
      if={e => e.metadata.annotations?.['microapp/hasGsmSecrets'] === 'true'}
    >
      <SecretsTab />
    </EntityLayout.Route>
    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/lifecycle" title="Lifecycle">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityAboutCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);
